import type { EventListItemDto } from '@/services/platform/api/model';
import { UNCATEGORISED_EVENT_CATEGORY } from './categories';

/**
 * Client-side category faceting for the ELP. Day bucketing is server-driven (contract v1.12:
 * `/events`'s `date` filter and `/events/dates` are both occurrence-aware — see
 * `useEventsByDay`); this file's remaining job is the category pill row, which the server
 * can't facet against the active date selection (`/events/categories` is hub-scoped only, not
 * date-aware).
 */

/** An event's category bucket for faceting — the real value, or the uncategorised sentinel. */
function categoryBucketOf(event: EventListItemDto): string {
  return event.eventCategory || UNCATEGORISED_EVENT_CATEGORY;
}

function matchesCategories(event: EventListItemDto, categories: string[]): boolean {
  if (categories.length === 0) return true;
  return categories.includes(categoryBucketOf(event));
}

/** Per-category event counts. Events with no `eventCategory` count under
 * `UNCATEGORISED_EVENT_CATEGORY` instead of being dropped. Pass a deduped list (`useEventsByDay`'s
 * `events`) — an event listed under several days must count once. */
export function categoryCounts(events: EventListItemDto[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const event of events) {
    const bucket = categoryBucketOf(event);
    counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  }
  return counts;
}

/** Narrows each day's events by the selected categories, dropping days left empty. The day
 * buckets themselves come from the server (`useEventsByDay`) — this only filters within them. */
export function applyCategoriesByDay(
  days: [string, EventListItemDto[]][],
  categories: string[]
): [string, EventListItemDto[]][] {
  const filtered: [string, EventListItemDto[]][] = [];
  for (const [key, items] of days) {
    const matching = items.filter(event => matchesCategories(event, categories));
    if (matching.length > 0) filtered.push([key, matching]);
  }
  return filtered;
}
